import { For, Show, createMemo, createSignal } from "solid-js";

import { useCreateStage, usePipelines, useReorderStages, useStages } from "~/lib/hooks";
import type { StageType } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

const STAGE_TYPE_LABEL: Record<StageType, string> = {
  open: "Open",
  won: "Won",
  lost: "Lost",
};

export function PipelinesPanel(props: Props) {
  const pipelines = usePipelines(props.wsKey);
  const [selectedId, setSelectedId] = createSignal<string | undefined>();

  const activePipelineId = createMemo<string | undefined>(() => {
    return selectedId() ?? pipelines.data?.find((p) => p.is_default)?.id ?? pipelines.data?.[0]?.id;
  });

  const stages = useStages(props.wsKey, activePipelineId);

  const createStage = useCreateStage(props.wsKey);
  const reorderStages = useReorderStages(props.wsKey);

  const [showAdd, setShowAdd] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const handleAddStage = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    const pipelineId = activePipelineId();
    if (!pipelineId) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const key = String(data.get("key") ?? "").trim();
    const name = String(data.get("name") ?? "").trim();
    if (!key || !name) return;
    try {
      await createStage.mutateAsync({
        pipelineId,
        body: {
          key,
          name,
          stage_type: (String(data.get("stage_type")) || "open") as StageType,
          probability_pct: Number(data.get("probability_pct") ?? 50),
        },
      });
      setShowAdd(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const moveStage = async (stageId: string, direction: -1 | 1) => {
    const pipelineId = activePipelineId();
    if (!pipelineId || !stages.data) return;
    const current = [...stages.data].sort((a, b) => a.order_index - b.order_index);
    const idx = current.findIndex((s) => s.id === stageId);
    if (idx < 0) return;
    const target = idx + direction;
    if (target < 0 || target >= current.length) return;
    const reordered = [...current];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    try {
      await reorderStages.mutateAsync({
        pipelineId,
        stageIds: reordered.map((s) => s.id),
      });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between flex-wrap gap-3">
          <h3 class="card-title text-base">Pipelines</h3>
          <div class="flex items-center gap-2">
            <label for="settings-pipeline" class="text-sm opacity-70">
              Pipeline:
            </label>
            <select
              id="settings-pipeline"
              class="select select-bordered select-sm"
              value={activePipelineId() ?? ""}
              onChange={(e) => setSelectedId(e.currentTarget.value || undefined)}
            >
              <For each={pipelines.data ?? []}>
                {(p) => (
                  <option value={p.id}>
                    {p.name}
                    {p.is_default ? " (default)" : ""}
                  </option>
                )}
              </For>
            </select>
          </div>
        </div>

        <Show when={pipelines.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-6 opacity-60">
            Loading…
          </div>
        </Show>

        <Show when={activePipelineId()}>
          <div class="overflow-x-auto mt-2">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Type</th>
                  <th>Probability</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <For
                  each={(stages.data ?? []).slice().sort((a, b) => a.order_index - b.order_index)}
                >
                  {(s, idx) => (
                    <tr>
                      <td class="text-xs opacity-60">{s.order_index}</td>
                      <td class="font-medium">{s.name}</td>
                      <td class="text-xs opacity-60">
                        <code>{s.key}</code>
                      </td>
                      <td>
                        <span
                          class={`badge badge-sm ${
                            s.stage_type === "won"
                              ? "badge-success"
                              : s.stage_type === "lost"
                                ? "badge-error"
                                : "badge-outline"
                          }`}
                        >
                          {STAGE_TYPE_LABEL[s.stage_type]}
                        </span>
                      </td>
                      <td class="text-sm">{s.probability_pct}%</td>
                      <td>
                        <div class="flex gap-1 justify-end">
                          <button
                            class="btn btn-ghost btn-xs"
                            aria-label={`Move ${s.name} up`}
                            disabled={idx() === 0 || reorderStages.isPending}
                            onClick={() => void moveStage(s.id, -1)}
                          >
                            ↑
                          </button>
                          <button
                            class="btn btn-ghost btn-xs"
                            aria-label={`Move ${s.name} down`}
                            disabled={
                              idx() === (stages.data?.length ?? 0) - 1 || reorderStages.isPending
                            }
                            onClick={() => void moveStage(s.id, 1)}
                          >
                            ↓
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>

        <Show when={error()}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            {error()}
          </div>
        </Show>

        <div class="flex justify-end mt-3">
          <button
            class="btn btn-primary btn-sm"
            disabled={!activePipelineId()}
            onClick={() => setShowAdd(true)}
          >
            Add stage
          </button>
        </div>

        <Show when={showAdd()}>
          <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="add-stage-title">
            <div class="modal-box">
              <h3 id="add-stage-title" class="font-bold text-lg mb-4">
                Add stage
              </h3>
              <form onSubmit={handleAddStage} class="flex flex-col gap-3">
                <input
                  name="key"
                  class="input input-bordered"
                  placeholder="key (lowercase, e.g. discovery)"
                  aria-label="Stage key"
                  pattern="[a-z0-9][a-z0-9-]*"
                  required
                />
                <input
                  name="name"
                  class="input input-bordered"
                  placeholder="Display name"
                  aria-label="Stage name"
                  required
                />
                <select name="stage_type" class="select select-bordered" aria-label="Stage type">
                  <option value="open">Open</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
                <label class="label">
                  <span class="label-text">Default probability ({"%"} of win)</span>
                </label>
                <input
                  name="probability_pct"
                  type="number"
                  min="0"
                  max="100"
                  value="50"
                  class="input input-bordered"
                  aria-label="Default probability"
                />
                <div class="modal-action">
                  <button type="button" class="btn btn-ghost" onClick={() => setShowAdd(false)}>
                    Cancel
                  </button>
                  <button type="submit" class="btn btn-primary" disabled={createStage.isPending}>
                    {createStage.isPending ? "Saving…" : "Add"}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        </Show>
      </div>
    </div>
  );
}
